import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import { EntityList, PageHead } from '../../components/EntityList';
import debtService from '../../services/debt.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import DebtDateEditor from './DebtDateEditor';

/**
 * Page shell for DebtDateEditor — this used to be an xl modal opened from
 * DebtDetails' actions menu. Only the debt's customId is fetched here (for the
 * heading); the editor still loads the full debt itself.
 */
function EditDebtStartDate() {
	const { t } = useLabels();
	const { id } = useParams();
	const navigate = useNavigate();
	const [customId, setCustomId] = useState('');

	const backTo = `/AdminScreen/debts/debt/${id}/detalles`;

	// Stable identity — DebtDateEditor's fetch effect keys off this prop, so a
	// fresh literal on every render of this page would refetch the debt.
	const initialData = useMemo(() => ({ _id: id }), [id]);

	useEffect(() => {
		let isCurrent = true;
		debtService
			.getDetails(id)
			.then(({ data }) => {
				if (isCurrent) setCustomId(data?.customId || '');
			})
			.catch((ex) => toast.error(getError(ex)));
		return () => {
			isCurrent = false;
		};
	}, [id]);

	const title = customId ? `${t('action.editStartDate')} — ${customId}` : t('action.editStartDate');

	return (
		<EntityList>
			<Helmet>
				<title>{t('action.editStartDate')}</title>
			</Helmet>
			<PageHead
				breadcrumb={[
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: t('entity.debt.plural'), to: '/AdminScreen/debts' },
					{ label: customId || t('entity.debt'), to: backTo },
					{ label: t('action.editStartDate') },
				]}
				title={title}
				subtitle='Ajustá la fecha de inicio y el primer vencimiento, y elegí qué hacer con las cuotas existentes.'
			/>
			<DebtDateEditor
				initialData={initialData}
				onSaved={() => navigate(backTo)}
				cancelHandler={() => navigate(backTo)}
			/>
		</EntityList>
	);
}

export default EditDebtStartDate;
